import { DynamicHeightScrollArea } from '@/components/atoms/DynamicHeightScrollArea';
import InputFloating from '@/components/atoms/InputFloating';
import SelectFloating from '@/components/atoms/SelectFloating';
import TextAreaFloating from '@/components/atoms/TextAreaFloating';
import DecisionModal from '@/components/custom/InfoPopups/DecisionModal';
import ErrorPopup from '@/components/custom/InfoPopups/ErrorPopup';
import SuccessPopup from "@/components/custom/InfoPopups/SuccessPopup";
import LoaderWithBackdrop from '@/components/custom/LoaderWithBackdrop';
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { useCrmConfigsByCrmProjectId } from '@/features/crm_config/hooks/useQuery/useCrmConfigsByCrmProjectId';
import { AddQuestionModal } from '@/features/question/components/organisms/AddQuestionModal';
import { EditQuestionModal } from '@/features/question/components/organisms/EditQuestionModal';
import { QuestionFormSchemaType } from '@/features/question/components/organisms/QuestionForm';
import { QuestionRow } from "@/features/question/components/organisms/QuestionRow";
import { useRemainingHeight } from "@/lib/hooks/useRemainingHeight";
import { TablesInsert } from '@/lib/supabase/supabase_types_override';
import { Tables } from "@/lib/supabase/supabase_types_override";
import { useAddSurveyWithQuestions } from '@/features/survey/hooks/useMutation/useAddSurveyWithQuestions';
import { useCrmStore, useOrganizationStore, useProjectStore, useUserStore } from '@/lib/zustand/useStore';
import { zodResolver } from '@hookform/resolvers/zod';
import { Loader } from "lucide-react";
import { useLayoutEffect, useRef, useState } from 'react';
import { useForm } from "react-hook-form";
import { useNavigate } from 'react-router';
import { Link, useParams } from 'react-router-dom';
import { z } from 'zod';
import { groupQuestionsByDependencies } from '@/features/question/hooks/useQuery/useGetQuestionsBySurveyId/utils/groupQuestionsByDependencies';


export function AddSurveyToProject() {
    const { organization } = useOrganizationStore();
    const { orgId } = useParams()
    const { project } = useProjectStore();

    /* 
    * Get Remaining Height for the ScrollArea
    * This is used to calculate the height of the scroll area based on the available space in the container.
    * Can be used in both columns using only Left/Right as a reference.
    */
    const { height: scrollAreaHeight, isLoading: isScrollCalculated, getHeight } = useRemainingHeight();
    /* 
    * Handle Right Column ScrollArea
    */
    const rightColumnButtonsRowRef = useRef<HTMLDivElement>(null);

    /* 
    * Logic for the Add Question
    */
    const navigate = useNavigate();
    const { setCrm } = useCrmStore(state => state);
    const userData = useUserStore(state => state);
    const [surveyQuestionsData, setSurveyQuestionsData] = useState<QuestionFormSchemaType[]>([]);

    const [showAddModal, setShowAddModal] = useState(false);


    const { mutateAsync: addSurveyWithQuestions, isPending: isPendingAddSurveyWithQuestions } = useAddSurveyWithQuestions({ crm_system: project?.crm_system });

    const surveySchema = z.object({
        name: z.string({ message: "Field is required" }).min(1, { message: "Field is required" }),
        description: z.string().optional().nullable(),
        crm_config_id: z.string().optional().nullable(),
    } satisfies Record<
        keyof Pick<
            TablesInsert<'survey'>,
            'name' | 'description' | 'crm_config_id'>,
        unknown
    >);
    type surveySchemaType = z.infer<typeof surveySchema>;
    const {
        register: surveyRegister,
        handleSubmit: surveyHandleSubmit,
        formState: surveyFormState,
        control: surveyControl,
        watch: surveyWatch,
    } = useForm<surveySchemaType>({ resolver: zodResolver(surveySchema) });
    const { errors: surveyErrors } = surveyFormState;
    useLayoutEffect(() => {
        if (project?.crm_project?.crm) {
            setCrm(project.crm_project.crm)
        }
    }, [project, setCrm])

    const onSubmitSurvey = async (values: { description: string, name: string, crm_config_id: string | null }) => {
        if (!surveyQuestionsData.length) {
            const userDecision = await DecisionModal({
                message: 'You are about to add a survey without any questions. Are you sure you want to proceed?',
                confirmButtonText: 'Save Without Adding Questions',
                cancelButtonText: 'Cancel'
            });
            if (!userDecision.isConfirmed) return;
        }

        if (!project?.id || !userData.id || !project) return;

        const projectID = project?.id;
        const crmID = project?.crm_id;
        const organisationID = project?.organisation_id;

        if (!projectID || !organisationID) return;

        const requestBody = {
            project_id: projectID,
            crm_id: crmID,
            organisation_id: Number(orgId),
            description: values.description,
            name: values.name,
            crm_config_id: values.crm_config_id ? Number(values.crm_config_id) : null,
            questions: surveyQuestionsData.map(question => {
                const questionCopy = { ...question };
                // TODO: check if isNew is needed
                delete questionCopy.isNew;
                return questionCopy;
            })
        }

        try {
            const addSurveyResponse = await addSurveyWithQuestions(requestBody);

            if (addSurveyResponse?.survey) {
                const surveyId = addSurveyResponse.survey.id;

                SuccessPopup('Survey added successfully!');
                navigate(`/organization/${organization?.id}/project/${project?.id}/survey/${surveyId}/manage-survey`);
            }
        } catch (error) {
            ErrorPopup('Error adding survey. Please try again later.');
        }
    }

    const addQuestionModalSubmitHandler = (questionBody: QuestionDataType) => {
        setSurveyQuestionsData(prevDate => [
            ...prevDate,
            {
                ...questionBody,
                id: crypto.randomUUID(),
                isNew: questionBody.id ? false : true,
                question_bank_ref_id: questionBody.id || null,
            }
        ]);
    }
    const { data: surveyCrmData } = useCrmConfigsByCrmProjectId(project?.crm_project_id);

    const onRemoveQuestion = (questionId: string) => {
        setSurveyQuestionsData(prevData => prevData.filter(question => question.id !== questionId).map(question => {
            if (question.selected_configuration?.conditionals?.where?.or) {
                question.selected_configuration.conditionals.where.or = question.selected_configuration.conditionals.where.or.map(condition => {
                    if (condition.and) {
                        condition.and = condition.and.filter(cond => !Object.keys(cond).includes(questionId));
                    }
                    return condition;
                });
            }
            return question;
        }));
    };

    // Edit functionality
    const [openEditQuestionModal, setOpenEditQuestionModal] = useState(false);
    const [selectedSurveyData, setSelectedSurveyData] = useState<Tables<'question'> | null>(null);
    const onEditQuestion = (questionId: string) => {
        setOpenEditQuestionModal(true);
        const selectedQuestion = surveyQuestionsData.find(question => question.id === questionId);
        if (selectedQuestion) {
            setSelectedSurveyData(selectedQuestion);
        }
    }
    const onSubmitEditQuestion = (questionBody: Tables<'question'>) => {
        setSurveyQuestionsData(prevData => prevData.map(question => {
            if (question.id === questionBody.id) {
                return {
                    ...question,
                    ...questionBody,
                    // isNew: false, // Mark as not new
                };
            }
            return question;
        }));
        setOpenEditQuestionModal(false);
    }
    const selectedCrmConfigId = surveyWatch('crm_config_id');
    console.log(surveyCrmData, selectedCrmConfigId)
    const selectedCrmConfig = surveyCrmData?.find(config => config.id === Number(selectedCrmConfigId));
    // Evaluate questions conditionals
    const fullGroupedQuestions = groupQuestionsByDependencies(surveyQuestionsData);

    return (
        <>
            {(isPendingAddSurveyWithQuestions) && (<LoaderWithBackdrop />)}

            <AddQuestionModal
                show={showAddModal}
                onHide={() => setShowAddModal(false)}
                onSubmit={addQuestionModalSubmitHandler}

                crm={project?.crm_project?.crm}
                crmProject={project?.crm_project}
                crmConfig={selectedCrmConfig}

                questions={surveyQuestionsData}
            />

            <EditQuestionModal
                show={openEditQuestionModal}
                onHide={() => setOpenEditQuestionModal(false)}
                onSubmit={onSubmitEditQuestion}

                questionData={selectedSurveyData}
                questionsFromOutside={surveyQuestionsData}

                crm={project?.crm_project?.crm}
                crmProject={project?.crm_project}
                crmConfig={selectedCrmConfig}
            />

            <div className="flex items-stretch flex-1 gap-4">

                <div className="flex flex-col flex-1" ref={getHeight}>

                    {(() => {
                        if (isScrollCalculated) return (
                            <div className="grid h-full place-items-center">
                                <Loader className="animate-spin" />
                            </div>
                        )

                        return (<div className="flex flex-col flex-1 gap-2">
                            <div className="flex justify-between pe-4" ref={rightColumnButtonsRowRef}>
                                <div className="flex items-center">
                                    <p className="text-xs">Add Survey</p>
                                </div>
                            </div>

                            <DynamicHeightScrollArea offset={50}>
                                <div className="flex flex-col gap-2">
                                    <div className="flex flex-col gap-2">

                                        <div className="flex flex-wrap">
                                            <div className="w-full p-0 ps-1 pe-1 sm:w-6/12">
                                                <div className="mb-2">
                                                    <InputFloating
                                                        label="Survey Name"
                                                        register={surveyRegister('name')}
                                                        errorMessage={surveyErrors.name?.message}
                                                        showError={true}
                                                    />
                                                </div>
                                                <div className="m-0 mb-2">
                                                    <SelectFloating
                                                        emptyLabel=""
                                                        hasEmptyOption={true}
                                                        emptyValue={null}

                                                        label="Select CRM Config (optional)"
                                                        placeholder="Select CRM Config"
                                                        name={'crm_config_id'}
                                                        errorMessage={surveyErrors.crm_config_id?.message}
                                                        showError={true}
                                                        dataArrayWithCustomVal={surveyCrmData?.map(config => [config.name, config.id])}
                                                        control={surveyControl}

                                                        disabled={!surveyCrmData?.length}
                                                    />
                                                </div>
                                            </div>
                                            <div className="w-full p-0 ps-1 pe-1 sm:w-6/12">
                                                <TextAreaFloating
                                                    label="Survey Description (optional)"
                                                    register={surveyRegister('description')}
                                                    errorMessage={surveyErrors.description?.message}
                                                    showError={true}
                                                />
                                            </div>
                                        </div>

                                        <div className="flex items-center justify-between">
                                            <p className="text-xs">Questions</p>

                                            <div className="flex justify-center gap-2">
                                                <Button className="ml-auto" size="sm" onClick={() => setShowAddModal(true)}>
                                                    Add Question
                                                </Button>
                                            </div>
                                        </div>
                                        <Separator className="mb-2" />

                                        <div className="flex flex-col w-full h-full gap-2 min-h-52 grow">
                                            {isPendingAddSurveyWithQuestions && (surveyQuestionsData?.length === 0) && (
                                                <div className="flex items-center justify-center h-full">
                                                    <Loader size="16" className={`animate-spin`} />
                                                </div>
                                            )}

                                            {!isPendingAddSurveyWithQuestions && (surveyQuestionsData?.length === 0) && (
                                                <div className="flex items-center justify-center h-full">
                                                    <p className="text-sm text-muted-foreground">No questions added yet. Click on the "Add Question" button above to add a question.</p>
                                                </div>
                                            )}

                                            {fullGroupedQuestions?.map((question) => (
                                                <QuestionRow
                                                    key={question.id}
                                                    question={question}
                                                    onEditQuestion={onEditQuestion}
                                                    onRemoveQuestion={onRemoveQuestion}
                                                />
                                            ))}

                                        </div>

                                    </div>
                                </div>
                            </DynamicHeightScrollArea>
                        </div>)
                    })()}

                </div>

            </div>

            <div className="flex">
                <div className="flex justify-end w-full gap-2">
                    {!project?.id && (<Button disabled={true} className="btn btn-orange h-100" variant="outline" type="button" onClick={() => { }}>Load Survey From File&nbsp;(.xlsx)</Button>)}

                    <Button asChild variant="secondary" size="sm">
                        <Link to={`/organization/${organization?.id}/project/${project?.id}/manage-project`}>Cancel</Link>
                    </Button>

                    {project?.id && (<Button size="sm" onClick={surveyHandleSubmit(onSubmitSurvey)}>Save Survey</Button>)}
                    {!project?.id && (<Button size="sm" onClick={surveyHandleSubmit(onSubmitSurvey)}>Add Survey</Button>)}
                </div>
            </div>

        </>
    )
}
